import { useCallback, useRef, useState } from "react";
import type { AxiosResponse } from "axios";
import { useAuthContext } from "../Auth";
import type {
  LeadFormOtpLeadType,
  LeadOtpSettings,
} from "../../types/types";
import { useStoreState } from "../../util/util";
import {
  clearPendingPayload,
  loadPendingPayload,
  savePendingPayload,
} from "./leadFormOtpStorage";
import {
  initLeadFormOtp,
  resendLeadFormOtp,
  verifyAndSubmitLeadFormOtp,
} from "./leadFormOtpApi";

export type LeadFormOtpStep = {
  otpSessionId: string;
  email: string;
  resendAvailableAt: string;
};

type UseLeadFormOtpOptions = {
  leadType: LeadFormOtpLeadType;
  sampleRequestId?: string;
};

export function shouldRequireLeadFormOtp(
  leadOtpSettings: LeadOtpSettings | null | undefined,
  roleIsGuest: boolean
): boolean {
  return !!leadOtpSettings && roleIsGuest;
}

export function useLeadFormOtp({
  leadType,
  sampleRequestId,
}: UseLeadFormOtpOptions) {
  const { roleIsGuest } = useAuthContext();
  const {
    storefront_id: storefrontId,
    storefront_metadata: { lead_otp_settings: leadOtpSettings },
  } = useStoreState();

  const [otpStep, setOtpStep] = useState<LeadFormOtpStep | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const otpStepRef = useRef<LeadFormOtpStep | null>(null);
  const isVerifyingRef = useRef(false);

  otpStepRef.current = otpStep;

  const shouldRequireOtp = shouldRequireLeadFormOtp(
    leadOtpSettings,
    roleIsGuest
  );

  const clearOtpState = useCallback(() => {
    if (storefrontId) {
      clearPendingPayload(storefrontId, leadType);
    }
    otpStepRef.current = null;
    setOtpStep(null);
  }, [storefrontId, leadType]);

  const beginOtpVerification = useCallback(
    async (formPayload: object, email: string, phone?: string) => {
      if (!storefrontId || !leadOtpSettings) {
        throw new Error("Lead form OTP is not configured");
      }
      setOtpLoading(true);
      try {
        savePendingPayload(
          storefrontId,
          leadType,
          formPayload as Record<string, unknown>
        );
        const response = await initLeadFormOtp({
          storefrontId,
          email,
          phone,
          leadType,
          sampleRequestId,
        });
        if (
          !response.otp_session_id ||
          !response.email ||
          !response.resend_available_at
        ) {
          throw new Error("OTP init response is missing required session fields");
        }
        const step: LeadFormOtpStep = {
          otpSessionId: String(response.otp_session_id),
          email: response.email,
          resendAvailableAt: response.resend_available_at,
        };
        otpStepRef.current = step;
        setOtpStep(step);
      } finally {
        setOtpLoading(false);
      }
    },
    [storefrontId, leadType, leadOtpSettings, sampleRequestId]
  );

  const verifyAndSubmit = useCallback(
    async (code: string): Promise<AxiosResponse> => {
      const activeStep = otpStepRef.current;
      if (!storefrontId || !activeStep) {
        throw new Error("OTP session is not active");
      }
      if (isVerifyingRef.current) {
        throw new Error("OTP verification is already in progress");
      }

      const payload = loadPendingPayload(storefrontId, leadType);
      if (!payload) {
        throw new Error("Pending form data expired");
      }

      isVerifyingRef.current = true;
      setOtpLoading(true);
      try {
        return await verifyAndSubmitLeadFormOtp(
          storefrontId,
          activeStep.otpSessionId,
          code,
          payload
        );
      } finally {
        isVerifyingRef.current = false;
        setOtpLoading(false);
      }
    },
    [storefrontId, leadType]
  );

  const completeOtpVerification = useCallback(() => {
    clearOtpState();
  }, [clearOtpState]);

  const resendOtp = useCallback(async () => {
    const activeStep = otpStepRef.current;
    if (!storefrontId || !activeStep) {
      return;
    }
    setOtpLoading(true);
    try {
      const response = await resendLeadFormOtp(
        storefrontId,
        activeStep.otpSessionId
      );
      if (
        !response.otp_session_id ||
        !response.email ||
        !response.resend_available_at
      ) {
        throw new Error("OTP resend response is missing required session fields");
      }
      const step: LeadFormOtpStep = {
        otpSessionId: String(response.otp_session_id),
        email: response.email,
        resendAvailableAt: response.resend_available_at,
      };
      otpStepRef.current = step;
      setOtpStep(step);
    } finally {
      setOtpLoading(false);
    }
  }, [storefrontId]);

  const cancelOtp = useCallback(() => {
    clearOtpState();
  }, [clearOtpState]);

  return {
    leadOtpSettings,
    shouldRequireOtp,
    otpStep,
    otpLoading,
    beginOtpVerification,
    verifyAndSubmit,
    completeOtpVerification,
    resendOtp,
    cancelOtp,
  };
}
